/**
 * Emergent Themes — hand-written RTK Query injection.
 *
 * The theme list itself comes from the generated assessments/results endpoint
 * (aggregate_type 32). This slice adds the lazy per-theme detail endpoint that
 * loads action plans + suggested interventions when a card is expanded.
 */
import { emptySplitApi } from '@/store/emptyApi';

export interface ThemeActionPlan {
  id: number;
  recommendation_name: string;
  recommendation_template_id: number | null;
  notes: string | null;
  team_id: number | null;
  leader_id: number | null;
  type_of: number;
  created_at: string;
  link_type: 'direct' | 'similar';
  similarity?: number;
}

export interface SuggestedIntervention {
  recommendation_template_id: number;
  recommendation_name: string;
  task_index: number;
  task: string;
  description: string;
  time?: string;
  time_estimate: string | null;
  practical_example?: string;
  practicalExample?: string;
  softSkills?: string;
  soft_skills?: string;
  area_of_focus?: string;
  areaOfFocus?: string;
  process_change_implementation_time?: string;
  processChangeImplementationTime?: string;
  how_to_implement?: string;
  howToImplement?: string;
  what_not_to_do?: string;
  whatNotToDo?: string;
  how_to_know_its_working?: string;
  howToKnowItsWorking?: string;
  suggested_process_changes?: string;
  suggestedProcessChanges?: string;
  process_changes?: string;
  processChanges?: string;
  references?: string;
  supportingScience?: string;
  supporting_science?: string;
  content?: any[];
  resources?: any[];
  why_question?: string[];
  relevance_score: number;
  relevance_label: string;
}

export interface EmergentThemeWithDetails {
  theme_uid: string;
  theme_title?: string;
  theme_description?: string;
  feedback_count?: number;
  signal_strength?: number;
  strength?: number;
  pillar_tags?: string[];
  team_id?: number;
  team_name?: string;
  action_plans?: ThemeActionPlan[];
  suggested_interventions?: SuggestedIntervention[];
}

export interface EmergentThemeTeamDetail {
  assessment_period_id: number;
  team_id: number;
  themes: EmergentThemeWithDetails[];
}

export interface EmergentThemeTeamDetailArgs {
  assessmentPeriodId: number;
  teamId: number;
}

export interface EmergentThemeDetail {
  theme_uid: string;
  assessment_period_id: number;
  action_plans: ThemeActionPlan[];
  suggested_interventions: SuggestedIntervention[];
}

export interface EmergentThemeDetailArgs {
  assessmentPeriodId: number;
  themeUid: string;
  teamId?: number;
}

const emergentThemesApi = emptySplitApi.injectEndpoints({
  endpoints: build => ({
    emergentThemeDetail: build.query<EmergentThemeDetail, EmergentThemeDetailArgs>({
      query: ({ assessmentPeriodId, themeUid, teamId }) => ({
        url: '/api/portal/v1/assessments/emergent-themes/detail/',
        params: {
          assessment_period_id: assessmentPeriodId,
          theme_uid: themeUid,
          team_id: teamId,
        },
      }),
    }),
    emergentThemeTeamDetail: build.query<EmergentThemeTeamDetail, EmergentThemeTeamDetailArgs>({
      query: ({ assessmentPeriodId, teamId }) => ({
        url: '/api/portal/v1/assessments/emergent-themes/team-detail/',
        params: {
          assessment_period_id: assessmentPeriodId,
          team_id: teamId,
        },
      }),
    }),
  }),
  overrideExisting: false,
});

export const { useEmergentThemeDetailQuery, useEmergentThemeTeamDetailQuery } = emergentThemesApi;
